<template>
  <v-card>
    <v-toolbar flat>
      <v-toolbar-title class="headline primary--text font-weight-medium">{{ title }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon>
        <v-icon class="heading grey--text text--darken-4" @click="close">mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-toolbar v-show="!uploading" flat>
      <v-file-input show-size counter multiple
                    v-model="filesToUpload"
                    label="Upload pictures"
                    prepend-icon="mdi-file-image"
                    accept="image/*"
                    @change="selectFiles"
                    class="pr-5" />
    </v-toolbar>
    <div v-show="uploading" class="pb-5">
      <v-card outlined class="mx-5">
        <v-card-text class="d-flex">
          <span>Uploading files: </span>
          <div v-for="progressItem in uploadProgress">
            <v-chip v-show="progressItem.inQueue || progressItem.uploading" label outlined class="mx-2 no-wrap">
              <span class="mr-2">{{progressItem.name}}</span>
              <span v-if="progressItem.uploading"><v-progress-circular size="16" indeterminate /></span>
              <span v-else><v-progress-circular size="16" :value="0" /></span>
            </v-chip>
          </div>
        </v-card-text>
      </v-card>
    </div>
    <div v-show="refreshingImages" class="pb-5">
      <v-card outlined class="mx-5">
        <v-card-text class="flex-inline">
          <span>Refreshing images</span>
          <div><v-progress-linear indeterminate></v-progress-linear></div>
        </v-card-text>
      </v-card>
    </div>
    <div>
      <div v-for="fileInfo in photoFileInfosGroup" class="px-5 pb-5">
        <span class="no-wrap">
          {{fileInfo.name}}
          <a :href="fileInfo.objectUrl" :download="fileInfo.name" target="_blank" class="icon-block no-text-decoration">
            <v-icon :size="iconSize"
                    class="mx-2">mdi-download</v-icon>
          </a>
          <v-icon :size="iconSize"
                  @click="openDeleteDialog(fileInfo.name)"
                  class="mx-2">mdi-delete</v-icon>
        </span>
        <div hidden>{{fileInfo.url}}</div>
        <div v-if="fileInfo.objectUrl">
          <v-img contain :src="fileInfo.objectUrl" />
        </div>
        <div v-else-if="fileInfo.message">
          {{fileInfo.message}}
        </div>
        <div v-else>
          <v-progress-circular indeterminate />
        </div>
      </div>
    </div>
    <!-- Confirm Dialog -->
    <ConfirmDialog v-model="deleteDialog"
                   title="Delete Photo"
                   description="Are you sure you want to delete this photo?"
                   :onConfirmClick="deletePhoto"
                   :onCloseClick="closeDeleteDialog" />
  </v-card>

</template>

<script>
  import ResponsiveSizeHelperMixin from '../../mixins/ResponsiveSizeHelperMixin';
  import ConfirmDialog from '../ui/ConfirmDialog.vue';
  import FileDownloadApi from '../../services/FileDownloadApi';

  export default {
    name: 'PhotoGallery',
    mixins: [ResponsiveSizeHelperMixin],
    components: { ConfirmDialog },
    props: {
      title: String,
      fileInfoGroup: String,
      photoFileNames: Array,
      getPhotoFileUrl: Function,
      onDeletePhoto: Function,
      onUploadPhoto: Function,
      onClose: Function
    },
    data: () => ({
      photoFileInfos: [],
      uploadProgress: [],
      filesToUpload: [],
      refreshingImages: false,
      deleteDialog: false,
      photoToDelete: null
    }),
    computed: {
      photoFileInfosGroup() {
        return this.photoFileInfos.filter(pfi => pfi.group === this.fileInfoGroup);
      },
      uploading() {
        return this.uploadProgress && this.uploadProgress.length;
      }
    },
    watch: {
      photoFileNames(newVal, oldVal) {
        this.generateNewPhotoFileInfos();
      }
    },
    methods: {
      close() {
        this.onClose();
      },
      async selectFiles(files) {
        if (!files || files.length === 0) return; // Stop the below reset of v-model filesToUpload looping

        // Having to do sequential uploads, as there are issues with entity updates colliding
        this.uploadProgress = files.map(file => ({ file, name: file.name, inQueue: true, uploading: false }));
        for (let item of this.uploadProgress) {
          item.inQueue = false;
          item.uploading = true;
          await this.onUploadPhoto(item.file);
          item.uploading = false;
        }

        this.uploadProgress = []
        this.filesToUpload = []; // clear the v-model to empty the input field (note calling reset() clears onChange, so don't do that)
      },
      generateNewPhotoFileInfos() {
        const fileInfoInGroupFinder = filename => pfi => pfi.name === filename && pfi.group === this.fileInfoGroup;
        const resultHandler = pfi => {
          const i = this.photoFileInfos.findIndex(fileInfoInGroupFinder(pfi.name)); // Find it again
          if (i !== -1) {
            this.photoFileInfos.splice(i, 1, pfi);
          } else {
            this.photoFileInfos.push(pfi);
          }
        };

        let p = Promise.resolve();

        for (let name of this.photoFileNames || []) {
          let fileInfo = this.photoFileInfos.find(fileInfoInGroupFinder(name));
          if (!fileInfo) {
            fileInfo = { name, group: this.fileInfoGroup, url: this.getPhotoFileUrl(name) };
          }

          if (!fileInfo.objectUrl) {
            this.refreshingImages = true;
            p = p.then(() => FileDownloadApi().get(fileInfo.url))            
              .then(response => {
                  const blob = new Blob([response.data]);
                  fileInfo.objectUrl = window.URL.createObjectURL(blob);
                  return fileInfo;
                },
                error => {
                  fileInfo.message = `Failed to download file ${fileName} for image objectUrl. <${error}>`;
                  return fileInfo;
                })
              .then(resultHandler, resultHandler); // Error result must also be a fileObject, but will have a message
          }
        }

        p.finally(() => this.refreshingImages = false);
      },
      openDeleteDialog(filename) {
        this.photoToDelete = filename;
        this.deleteDialog = true;
      },
      closeDeleteDialog() {
        this.deleteDialog = false;
        this.photoToDelete = null;
      },
      deletePhoto() {
        var filename = this.photoToDelete;
        this.onDeletePhoto(filename, this.fileInfoGroup)
          .then(() => {
            const i = this.photoFileInfos.findIndex(pfi => pfi.name === filename && pfi.group === this.fileInfoGroup);
            if (i !== -1) {
              this.photoFileInfos.splice(i, 1);
            }
          });
        this.closeDeleteDialog();
      }
    },
    created() {
      this.generateNewPhotoFileInfos();
    },
    beforeDestroy() {
      // revoke object urls (https://developer.mozilla.org/en-US/docs/Web/API/URL/revokeObjectURL) to free memory
      // Required at least for retrieved files (https://stackoverflow.com/a/49346614)
      for (let pfi of this.photoFileInfos || []) {
        window.URL.revokeObjectURL(pfi.objectUrl);
      }

      this.photoFileInfos = []; // Bit unnecessary, but I do like to clean up after myself
    }
  };
</script>

<style>
  .no-wrap {
    overflow: hidden;
    white-space: nowrap;
  }

  .no-text-decoration {
    text-decoration: none;
  }
</style>
